import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import DoubleRightIcon from '../../../assets/icons/JSX/DoubleRightIcon'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import GreenTick from '../../../assets/v2/JSX/GreenTick'
import LeftThreeDot from '../../../assets/v2/JSX/LeftThreeDot'
import RedCross from '../../../assets/v2/JSX/RedCross'
import RightThreeDot from '../../../assets/v2/JSX/RightThreeDot'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import RemainingCompanies from '../../../components/v2/RemainingCompanies'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import WarningMessage from '../../../components/v2/WarningMessage'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import ToggleSwitcher from '../../../components/v2/buttons/ToggleSwitcher'
import PetProgressbar from '../../../components/v2/progrees-bars/PetProgressBar'
import { georgianFormatter } from '../../../domain/types'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import {
  PetState,
  setOffer,
  setOfferTotalPrice,
  setPayment,
  setPaymentIds,
  setPaymentScheduleMethods,
  setPetPaymentSchedule,
} from '../../../store/petSlice'

const PetOffer = () => {
  //
  const { t } = useTranslation()
  //
  const dispatch = useDispatch()
  //
  const navigate = useNavigate()
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const { screenHeader, filter } = useAppSelector((data: { pet: PetState }) => data.pet)
  //
  // custom price scroll position in number
  const [scrollPosition, setScrollPosition] = useState(0)
  const handleScroll = () => {
    const position = window.pageYOffset
    setScrollPosition(position)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  })
  //
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetPetPageInfoQuery('pet-best-offer')
  //
  const pageTitle = pageInfo?.translations[lang]?.title
  const stepName = pageInfo?.translations[lang]?.stepName
  const listItem = pageInfo?.listItems?.find((x) => x.type === 'warning-text')
  //
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } = api.useGetTplPaymentMethodsQuery(undefined)
  //
  const { data: petServices, isLoading: isPetServicesLoading } = api.useGetPetServicesQuery({
    query: `status=active`,
  })
  //
  const sortedPetServices = petServices?.slice().sort((a, b) => a.priority - b.priority)
  //
  // set payment method in store callback
  const setPaymentMethodInStore = useCallback(() => {
    if (paymentMethods && paymentMethods.length >= 1) {
      dispatch(setPaymentScheduleMethods(paymentMethods))
    }
  }, [dispatch, paymentMethods])
  //
  //
  // set payment method in store action
  useEffect(() => {
    setPaymentMethodInStore()

    return () => {}
  }, [setPaymentMethodInStore])
  //
  // set payment ids
  useEffect(() => {
    const paymentIds = paymentMethods
      ?.filter(
        (x) => x.slug.split('-')[0].toUpperCase() === filter.seletedPaymentMethod?.type.split('-')[0].toUpperCase(),
      )
      .map((x) => x.id)

    if (paymentIds) {
      dispatch(setPaymentIds(paymentIds))
    }
  }, [filter?.seletedPaymentMethod, paymentMethods, dispatch])
  //
  //
  const { data: petCategories, isLoading: isPetCategoryLoading } = api.useGetPetCategoriesQuery(undefined)
  //
  const [getPetOffer, { isLoading: isPetOfferLoading, data: petOffer }] = api.usePetBestOfferMutation()
  //
  useEffect(() => {
    if (
      filter.petCategoryId &&
      (filter.breedId || filter.breedId === null) &&
      ((filter?.petInfo?.month !== null && filter?.petInfo?.month !== 0) ||
        (filter?.petInfo?.age !== null && filter?.petInfo?.age !== 0)) &&
      (filter?.breedId === null ? filter?.petInfo?.breedOther !== '' : true) &&
      filter.dates.start &&
      filter.dates.end &&
      filter?.paymentIds.length > 0
    ) {
      const GetCompaniesFunction = async () => {
        try {
          await getPetOffer({
            animalCategoryId: filter?.petCategoryId as number,
            breedId: filter.breedId,
            petAge: {
              month: filter?.petInfo?.month as number,
              year: filter?.petInfo?.age as number,
            },
            startDate: filter.dates.start as string,
            endDate: filter.dates.end as string,
            otherBreedName: filter?.breedId === null ? filter?.petInfo?.breedOther : '',
            paymentScheduleTypeIds: filter?.paymentIds,
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (GetCompaniesFunction) (single)')
          }
        }
      }
      //
      GetCompaniesFunction()
      //
    }

    return () => {}
  }, [
    getPetOffer,
    filter?.breedId,
    filter?.petCategoryId,
    filter?.petInfo?.age,
    filter?.petInfo?.month,
    filter?.dates.start,
    filter?.dates?.end,
    filter?.paymentIds,
    filter?.petInfo?.breedOther,
  ])
  //
  const setOfferInStore = useCallback(() => {
    const bestOffer = petOffer?.find((x) => x.isBestOption === true)
    //
    if (bestOffer) {
      dispatch(setOffer(bestOffer))
    }
    //
  }, [petOffer, dispatch])

  //
  useEffect(() => {
    //
    setOfferInStore()
    //
    return () => {}
    //
  }, [setOfferInStore])
  //
  const setPaymentInStore = useCallback(() => {
    //
    if (petOffer && petOffer.length >= 1 && filter?.offer) {
      dispatch(
        setPayment({
          firstPaymentAmount: filter.offer.firstPaymentAmount,
          monthlyPaymentAmount: filter.offer.monthlyPaymentAmount,
          totalPaymentAmount: filter.offer.totalPaymentAmount,
        }),
      )
      //
    }
  }, [petOffer, dispatch, filter.offer])
  //
  useEffect(() => {
    //
    setPaymentInStore()
    //
    return () => {}
  }, [setPaymentInStore])
  //
  const _Price =
    petOffer && petOffer?.length >= 1
      ? filter?.offer?.paymentScheduleType?.slug === 'ONE-TIME'
        ? georgianFormatter.format(filter.payment?.totalPaymentAmount as number)
        : georgianFormatter.format(filter.payment?.firstPaymentAmount as number)
      : '0'
  //
  useEffect(() => {
    if (_Price) {
      dispatch(setOfferTotalPrice(_Price?.toString()))
    }
    //
    return () => {}
  }, [_Price, dispatch])
  //
  //
  const parsedPaymentScheduleType =
    typeof filter?.offer?.paymentScheduleType?.translations === 'string'
      ? JSON.parse(filter?.offer?.paymentScheduleType?.translations)
      : filter?.offer?.paymentScheduleType?.translations
  //
  // auto navigate first page
  useEffect(() => {
    if (filter?.petCategoryId === null) {
      navigate('/choose-pet')
    }
    //
    return () => {}
  }, [filter?.petCategoryId, navigate])
  //
  const MainLoading =
    isPageInfoLoading || isPetOfferLoading || isPaymentMethodsLoading || isPetCategoryLoading || isPetServicesLoading
  //
  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />

      <Container>
        <Content>
          <Title text={pageTitle} />

          <PetProgressbar stepName={stepName} />

          <Schedule>
            <span
              className="payment-montly"
              style={{
                color: filter.seletedPaymentMethod?.type === 'MONTHLY' ? Colors.primaryBlack : Colors.nevadaGrey,
              }}
            >
              {t('monthly')}
            </span>
            <ToggleSwitcher
              activeRedBackground={true}
              defaultValue={filter.seletedPaymentMethod?.type === 'MONTHLY' ? false : true}
              onChange={(value) => {
                if (value === true && paymentMethods) {
                  //
                  return [
                    dispatch(
                      setPetPaymentSchedule({
                        id: paymentMethods?.find((x) => x.slug === 'ONE-TIME')?.id || 0,
                        type: 'ONE-TIME',
                      }),
                    ),
                  ]
                } else {
                  //
                  return [
                    dispatch(
                      setPetPaymentSchedule({
                        id: paymentMethods?.find((x) => x.slug === 'MONTHLY')?.id || 0,
                        type: 'MONTHLY',
                      }),
                    ),
                  ]
                }
              }}
            />
            <span
              className="payment-annual"
              style={{
                color: filter.seletedPaymentMethod?.type === 'ONE-TIME' ? Colors.primaryBlack : Colors.nevadaGrey,
              }}
            >
              {t('annual')}
            </span>
          </Schedule>

          <Price
            $pricePosition={scrollPosition}
            style={{
              userSelect: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
              pointerEvents: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
              opacity: petOffer && petOffer.length >= 1 ? 'unset' : '0.6',
            }}
          >
            <LeftThreeDot />
            <div>
              {filter?.offerTotalPrice || 0} {'₾'}
            </div>
            <RightThreeDot />
          </Price>

          {petOffer && petOffer.length >= 1 ? null : (
            <RemainingCompanies
              style={{
                marginInline: 'auto',
                width: 'fit-content',
                marginTop: '30px',
              }}
              text={t('offerNotFoundChangeSetting')}
            />
          )}

          <Block>
            {petOffer && petOffer?.length >= 1 ? (
              <>
                <OfferTitle>
                  {t('see')} <b>{filter?.offer?.translations[lang]?.title}</b> {t('seeCompanyOffer')}
                </OfferTitle>

                <div style={{ display: 'flex', gap: '15px', marginTop: '30px' }}>
                  <CompanyImg src={filter?.offer?.translations[lang]?.logo} alt="company-image" />

                  <CompanyName>{filter?.offer?.brandName[lang]?.title}</CompanyName>
                </div>

                <HR />
              </>
            ) : null}

            <div
              style={{
                userSelect: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
                pointerEvents: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
                opacity: petOffer && petOffer.length >= 1 ? 'unset' : '0.6',
              }}
            >
              <SBlock
                style={{
                  color: Colors.primaryBlack,
                }}
              >
                <SLeft>{t('petService')}</SLeft>
                <SRight>
                  <SInnerLeft>{t('petCoverage')}</SInnerLeft>
                  <SInnerRight>{t('petLimit')}</SInnerRight>
                </SRight>
              </SBlock>

              {filter?.offer?.packageServicesWithCommon?.map((item, index) => (
                <SBlock key={index}>
                  <SLeft>{item?.packageService?.translations[lang]?.name}</SLeft>
                  <SRight>
                    <SInnerLeft>{item?.common?.cover}</SInnerLeft>
                    <SInnerRight>{item?.common?.translations[lang]?.limit}</SInnerRight>
                  </SRight>
                </SBlock>
              ))}

              <HR />

              <ServiceBlock>
                <ServiceHead>
                  <LeftTitle>{t('petServiceWaiting')}</LeftTitle>
                  <RigthTitle>
                    <div></div>
                    <div>{t('waitingPeriod')}</div>
                  </RigthTitle>
                </ServiceHead>
                {/*  */}
                {filter?.offer?.packageServicesWithCommon
                  ?.filter((x) => x.common?.translations[lang]?.waitPeriod !== undefined)
                  ?.map((item, index) => (
                    <ServiceBody key={index}>
                      <Left>{item?.packageService?.translations[lang]?.name}</Left>
                      <Right>
                        <div></div>
                        <div>{item?.common?.translations[lang]?.waitPeriod}</div>
                      </Right>
                    </ServiceBody>
                  ))}

                <WarningMessage variant="gray" text={listItem?.translations[lang]?.body}></WarningMessage>
              </ServiceBlock>

              <HR />

              <ServiceBlock>
                <ServiceHead>
                  <LeftTitle>{t('petServicesList')}</LeftTitle>
                  <Right></Right>
                </ServiceHead>
                {/*  */}
                {sortedPetServices?.map((item, index) => (
                  <ServiceBody key={index}>
                    <Left>{item?.translations[lang]?.name}</Left>
                    <Right>
                      <div></div>
                      <div>
                        {filter?.offer?.petServicesWithCommon?.some((x) => x.petService?.id === item?.id) ? (
                          <GreenTick />
                        ) : (
                          <RedCross />
                        )}
                      </div>
                    </Right>
                  </ServiceBody>
                ))}
              </ServiceBlock>

              <HR />

              <ServiceBlock>
                <ServiceBody>
                  <Left>{t('pet')}</Left>
                  <Right>
                    <div></div>
                    <div>{petCategories?.find((x) => x.id === filter?.petCategoryId)?.translations[lang]?.name}</div>
                  </Right>
                </ServiceBody>

                <ServiceBody>
                  <Left>{t('policyDuration')}</Left>
                  <Right>
                    <div></div>
                    <div>
                      {filter?.offer?.days} {t('day')}
                    </div>
                  </Right>
                </ServiceBody>

                <ServiceBody>
                  <Left>{t('startDate')}</Left>
                  <Right>
                    <div></div>
                    <div>{dayjs(filter?.dates?.start).format('D MMMM YYYY')}</div>
                  </Right>
                </ServiceBody>
              </ServiceBlock>

              {petOffer && petOffer.length >= 1 && filter?.seletedPaymentMethod?.type === 'MONTHLY' ? (
                <>
                  <HR />

                  <ServiceBlock>
                    <ServiceBody>
                      <Left>{t('paymentSchedule')}</Left>
                      <Right>
                        <div></div>
                        <div>{parsedPaymentScheduleType && parsedPaymentScheduleType[lang]?.title}</div>
                      </Right>
                    </ServiceBody>

                    <ServiceBody>
                      <Left>{t('currentPayment')}</Left>
                      <Right>
                        <div></div>
                        <div>{filter.offer?.firstPaymentAmount} ₾</div>
                      </Right>
                    </ServiceBody>

                    <ServiceBody>
                      <Left>{t('eachPayment')}</Left>
                      <Right>
                        <div></div>
                        <div>{filter?.offer?.monthlyPaymentAmount} ₾</div>
                      </Right>
                    </ServiceBody>

                    <ServiceBody>
                      <Left>{t('annualPaymentTitle')}</Left>
                      <Right>
                        <div></div>
                        <div>{filter?.offer?.totalPaymentAmount} ₾</div>
                      </Right>
                    </ServiceBody>
                  </ServiceBlock>
                </>
              ) : null}

              <HR />

              <Summary>
                <PriceInfo>
                  <PriceText>
                    {filter?.offerTotalPrice || 0}
                    {'₾'}
                  </PriceText>
                </PriceInfo>

                <ATagStyle
                  style={{
                    userSelect: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
                    pointerEvents: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
                    opacity: petOffer && petOffer.length >= 1 ? 'unset' : '0.6',
                  }}
                  href={filter.offer?.termsPath?.translations?.[lang]?.url || ''}
                  target="_blank"
                >
                  <div>{t('policyTerms')}</div>
                </ATagStyle>
              </Summary>
            </div>
          </Block>

          <Buttons>
            <NavigationButton text={`${t('back')}`} buttonType="prev" onClick={() => navigate('/pet-start-date')} />
            <NavigationButton
              text={`${t('next')}`}
              disabled={petOffer && petOffer?.length >= 1 ? false : true}
              buttonType="next"
              onClick={() => navigate('/pet-insurer')}
            />
          </Buttons>
        </Content>

        <div
          style={{
            userSelect: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
            pointerEvents: petOffer && petOffer.length >= 1 ? 'unset' : 'none',
            opacity: petOffer && petOffer.length >= 1 ? 'unset' : '0.6',
          }}
        >
          <HR />

          <MessageBox>
            <MessageContent>
              <MessageHead>
                <span>{t('whatIsCovered')}</span>
              </MessageHead>
              {filter?.offer?.coverages?.map((coverage, coverageIndex) => (
                <MessageText key={coverageIndex}>
                  <DoubleRightIcon /> <span>{coverage?.translations[lang]?.body}</span>
                </MessageText>
              ))}
            </MessageContent>
          </MessageBox>

          <MessageBox>
            <MessageContent>
              <MessageHead>
                <span>{t('notCovered')}</span>
              </MessageHead>
              {filter?.offer?.importantTerms?.map((item, index) => (
                <MessageText key={index}>
                  <DoubleRightIcon /> <span>{item?.translations[lang]?.body}</span>
                </MessageText>
              ))}
            </MessageContent>
          </MessageBox>
        </div>
      </Container>
      {scrollPosition <= 0 && <BackgroundIcon />}
    </Wrapper>
  )
}
//
//
const PriceText = styled.span`
  font-size: 28px;
  color: ${Colors.primaryBlack};
  text-decoration: none;
  font-weight: 700;
  letter-spacing: 2px;
`
const PriceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${Responsive.mobile} {
    display: block;
  }
`
//
const SInnerRight = styled.div`
  min-width: 120px;
  text-align: right;

  ${Responsive.mobile} {
    min-width: 80px;
  }
`
const SInnerLeft = styled.div`
  text-align: right;
  ${Responsive.mobile} {
    min-width: 50px;
  }
`
const SRight = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`
const SLeft = styled.div`
  flex-basis: 60%;

  ${Responsive.mobile} {
    flex-basis: unset;
  }
`
const SBlock = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 14px;
  font-size: 14px;
  color: ${Colors.nevadaGrey};
`
const CompanyName = styled.div`
  font-size: 18px;
  color: ${Colors.primaryBlack};
`
const CompanyImg = styled.img`
  height: 26px;
`
const Schedule = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px 0 27px 0;
  user-select: none;
  position: relative;
  width: fit-content;
  margin-inline: auto;

  .payment-montly {
    right: 35px;
  }

  .payment-annual {
    left: 35px;
  }

  span {
    position: absolute;
    font-size: 14px;
    /* color: ${Colors.primaryBlack}; */
    :first-child {
      padding-right: 10px;
    }
    :last-child {
      padding-left: 10px;
    }
  }
  svg {
    color: ${Colors.secondaryGrey};
  }

  ${Responsive.mobile} {
    margin-block: 24px;
  }
`
const MessageText = styled.div`
  font-size: 14px;
  color: ${Colors.primaryBlack};
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  /*  */
  &:last-child {
    margin-bottom: 0px;
  }

  /* message text span */
  span {
    margin-left: 15px;
    color: ${Colors.primaryGrey};
  }

  /* message checkmark icon & svg */
  svg {
    min-width: 20px;
    width: 20px;
    height: 20px;
    margin-left: -3px;
    color: ${Colors.primaryGrey};
  }

  /*  */
  ${Responsive.mobile} {
    /* message checkmark icon & svg */
    svg {
      margin-left: 0px;
      min-width: 15px;
      width: 15px;
    }

    /* message text span */
    span {
      margin-left: 20px;
      color: ${Colors.primaryGrey};
      line-height: 20px;
    }
  }
`
const MessageHead = styled.div`
  font-size: 18px;
  color: ${Colors.primaryBlack};
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  ${Responsive.mobile} {
    font-size: 14px;
    margin-bottom: 15px;

    /* message text span */
    span {
      margin-left: 15px;
    }
  }
`
const MessageContent = styled.div`
  margin-bottom: 33px;
`
const MessageBox = styled.div`
  :first-child {
    border-top: 1px solid ${Colors.secondaryGrey};
    margin-top: 80px;
  }
  padding-top: 30px;
  ${Responsive.tablet} {
    max-width: 700px;
    margin: 0 auto;
  }
`
// const Percentage = styled.span`
//   margin-left: 10px;
//   margin-top: 3px;
//   font-size: 14px;
//   color: ${Colors.nevadaGrey};
//   text-align: left;

//   b {
//     color: ${Colors.primaryRed};
//   }

//   ${Responsive.mobile} {
//     margin-left: 0px;
//   }
// `
const ATagStyle = styled.a`
  font-size: 14px;
  line-height: 10px;
  color: ${Colors.primaryBlack};
  text-align: right;
  div {
    line-height: 18px;
    &:last-child {
      margin-left: 5px;
      display: flex;
      justify-content: center;
      align-items: center;
      svg {
        width: 16px;
        height: 10px;
      }
    }
  }
  /*  */
  &:hover {
    color: ${Colors.primaryBlack};
    div {
      svg {
        color: ${Colors.primaryRed};
      }
    }
  }
`
const Summary = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${Colors.nevadaGrey};
`
const ServiceBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
const Right = styled.div`
  flex-basis: 30%;
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: ${Colors.nevadaGrey};
  ${Responsive.mobile} {
    flex-basis: 45%;
  }
`
const RigthTitle = styled.div`
  flex-basis: 30%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 13px;
  font-size: 14px;
  color: ${Colors.primaryBlack};

  ${Responsive.mobile} {
    flex-basis: 45%;
  }
`
const LeftTitle = styled.div`
  flex-basis: 70%;
  margin-bottom: 13px;
  font-size: 14px;
  color: ${Colors.primaryBlack};

  ${Responsive.mobile} {
    flex-basis: 55%;
  }
`

const Left = styled.div`
  flex-basis: 70%;
  font-size: 14px;
  color: ${Colors.nevadaGrey};
  ${Responsive.mobile} {
    flex-basis: 55%;
  }
`
const ServiceHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${Colors.primaryBlack};
  font-size: 14px;
`
const ServiceBody = styled(ServiceHead)`
  color: ${Colors.nevadaGrey};
  margin-block: 7px;
`

const HR = styled.hr`
  margin-block: 24px;
`
const OfferTitle = styled.h6`
  font-size: 18px;
  color: ${Colors.primaryBlack};
  margin-bottom: 0px;

  ${Responsive.mobile} {
    font-size: 14px;
  }
`
const Price = styled.div<{ $pricePosition: number }>`
  position: ${({ $pricePosition }) => ($pricePosition >= 170 ? 'fixed' : 'sticky')};
  top: 100px;
  right: 5px;
  width: fit-content;
  margin: 0 auto;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  pointer-events: none;

  /* text */
  div {
    width: fit-content;
    height: 64px;
    background-color: ${Colors.primaryRed};
    border-radius: 40px;
    color: ${Colors.white};
    font-size: 20px;
    padding-inline: 40px;
    margin-inline: 10px;
    display: grid;
    place-items: center;
    box-shadow: 0 4px 35px 0 rgba(254, 95, 85, 0.32);
  }

  svg {
    height: 64px;
  }

  /* Responsive */
  ${Responsive.laptop} {
    top: 120px;
    margin: 0 auto;

    div {
      height: 48px;
      font-size: 16px;
      margin-inline: 0px;
    }

    svg {
      height: 40px;
    }
  }

  ${Responsive.tablet} {
    top: 100px;
    margin: 0 auto;

    div {
      height: 48px;
      font-size: 16px;
      margin-inline: 0px;
    }

    svg {
      display: ${({ $pricePosition }) => ($pricePosition >= 160 ? 'none' : 'unset')};
      height: 40px;
    }
  }

  ${Responsive.mobile} {
    top: 80px;
    margin: 0 auto;

    div {
      height: 48px;
      font-size: 16px;
      margin-inline: 0px;
    }

    svg {
      display: ${({ $pricePosition }) => ($pricePosition >= 100 ? 'none' : 'unset')};
      height: 40px;
    }
  }
`
const Block = styled.div`
  max-width: 800px;
  width: calc(100vw - 96px);
  padding: 48px;
  background-color: ${Colors.white};
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  margin-block: 48px;
  border-radius: 8px;

  ${Responsive.mobile} {
    margin-block: 33px;
    padding: 20px;
    width: calc(100vw - 40px);
  }
`
const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`
const Content = styled.div`
  padding-block: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: sticky;
  z-index: 1;

  ${Responsive.mobile} {
    padding-block: 30px;
  }
`
const Wrapper = styled.div`
  /*  */
`
//
export default PetOffer
